import { apiGet, apiSend } from "./apiClient";

export interface UserListItem {
  user_oid: string;
  email: string | null;
  name: string | null;
  provider: string | null;
  customer_id: number | null;
  last_login: string | null;
  device_count: number;
}

export interface UserDeviceItem {
  device_id: string;
  device_display_name: string | null;
  is_active: boolean;
  subscribed: boolean;
}

export interface DeviceItem {
  device_id: string;
  display_name: string | null;
}

export interface CustomerSummary {
  id: number;
  name: string;
}

export interface PendingInvitation {
  id: number;
  email: string;
  customer_id: number;
  token: string;
  link: string;
  created_at: string | null;
  created_by_user_oid: string | null;
}

export interface InviteResponse {
  status: "claimed" | "pending";
  invitation: PendingInvitation | null;
  user: UserListItem | null;
}

export interface MeInfo {
  user_oid: string;
  email: string | null;
  name: string | null;
  user_type: "internal" | "external";
  customer_id: number | null;
  can_manage_users: boolean;
}

/** Internal users may pass ?customer_id=… to scope all writes/reads to that
 *  company. External users must omit it (backend rejects mismatches). */
function cidQuery(customerId?: number, separator: "?" | "&" = "?"): string {
  return customerId != null
    ? `${separator}customer_id=${encodeURIComponent(String(customerId))}`
    : "";
}

export function fetchMe() {
  return apiGet<MeInfo>("/users/me");
}

export function fetchAllCustomers() {
  return apiGet<CustomerSummary[]>("/users/customers");
}

export function fetchCompanyUsers(customerId?: number) {
  return apiGet<UserListItem[]>(`/users${cidQuery(customerId)}`);
}

export function addUserByEmail(email: string, customerId?: number) {
  return apiSend<UserListItem>(
    "POST",
    `/users/add-by-email${cidQuery(customerId)}`,
    { email },
  );
}

export function removeUser(userOid: string, customerId?: number) {
  return apiSend<void>(
    "DELETE",
    `/users/${encodeURIComponent(userOid)}${cidQuery(customerId)}`,
  );
}

export function fetchUserDevices(userOid: string, customerId?: number) {
  return apiGet<UserDeviceItem[]>(
    `/users/${encodeURIComponent(userOid)}/devices${cidQuery(customerId)}`,
  );
}

export function assignDevice(
  userOid: string,
  deviceId: string,
  customerId?: number,
) {
  return apiSend<UserDeviceItem>(
    "POST",
    `/users/${encodeURIComponent(userOid)}/devices${cidQuery(customerId)}`,
    { device_id: deviceId },
  );
}

export function unassignDevice(
  userOid: string,
  deviceId: string,
  customerId?: number,
) {
  return apiSend<void>(
    "DELETE",
    `/users/${encodeURIComponent(userOid)}/devices/${encodeURIComponent(
      deviceId,
    )}${cidQuery(customerId)}`,
  );
}

/** Internal-only: devices currently in a customer's pool (dbo.CustomerDevices). */
export function fetchCustomerPool(customerId: number) {
  return apiGet<DeviceItem[]>(
    `/users/customers/${encodeURIComponent(String(customerId))}/devices`,
  );
}

/** Internal-only: devices not assigned to any customer — addable to a pool. */
export function fetchUnassignedDevices() {
  return apiGet<DeviceItem[]>("/users/devices/unassigned");
}

/** Internal-only: add a device to a customer's pool. */
export function assignDeviceToCustomer(customerId: number, deviceId: string) {
  return apiSend<DeviceItem>(
    "POST",
    `/users/customers/${encodeURIComponent(String(customerId))}/devices`,
    { device_id: deviceId },
  );
}

/** Internal-only: remove a device from a customer's pool. */
export function unassignDeviceFromCustomer(customerId: number, deviceId: string) {
  return apiSend<void>(
    "DELETE",
    `/users/customers/${encodeURIComponent(String(customerId))}/devices/${encodeURIComponent(deviceId)}`,
  );
}

export interface DeviceCustomerIndex {
  /** device_id → customer name, for every device in some customer's pool. */
  byDevice: Record<string, string>;
  /** Customer names that actually own at least one device, sorted. */
  names: string[];
}

/**
 * Build a device → customer-name index by walking every customer's device pool.
 *
 * Internal (Oso staff) only — `/users/customers` is staff-scoped, so this
 * resolves to `null` for external users, whose devices all belong to their own
 * single company and for whom a customer column carries no information.
 */
export async function fetchDeviceCustomerIndex(): Promise<DeviceCustomerIndex | null> {
  let customers: CustomerSummary[];
  try {
    customers = await fetchAllCustomers();
  } catch {
    return null; // not permitted (external user) — caller hides the column
  }
  const pools = await Promise.all(
    customers.map((c) => fetchCustomerPool(c.id).catch(() => [] as DeviceItem[])),
  );
  const byDevice: Record<string, string> = {};
  const names = new Set<string>();
  customers.forEach((c, i) => {
    for (const d of pools[i]) {
      byDevice[d.device_id] = c.name;
      names.add(c.name);
    }
  });
  return {
    byDevice,
    names: Array.from(names).sort((a, b) => a.localeCompare(b)),
  };
}

export interface AddableUserItem {
  user_oid: string;
  email: string;
  name: string | null;
  /** Company they're in today — null when they belong to no company yet. */
  customer_id: number | null;
  customer_name: string | null;
}

/** Internal-only: every signed-in user NOT already in `customerId` — the
 *  option list behind the internal "Add crew" picker. Users already in another
 *  company are included (flagged with `customer_name`); adding one still fails
 *  with a 409 until they're removed there. */
export function fetchAddableUsers(customerId: number) {
  return apiGet<AddableUserItem[]>(
    `/users/addable?customer_id=${encodeURIComponent(String(customerId))}`,
  );
}

export function fetchAssignableDevices(userOid?: string, customerId?: number) {
  let qs = "";
  if (userOid) qs = `?user_oid=${encodeURIComponent(userOid)}`;
  if (customerId != null) qs += cidQuery(customerId, qs ? "&" : "?");
  return apiGet<DeviceItem[]>(`/users/devices/available${qs}`);
}
